<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaAvatar from '../base/GaiaAvatar.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';
import GaiaCheckbox from '../base/GaiaCheckbox.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  /** Secondary line under the label, e.g. the email or contract count. */
  meta: {
    type: String,
    default: undefined,
  },
  /**
   * Colour the meta line when the choice carries a consequence — the re-run
   * scope dialog flags "All contracts" amber for cost.
   */
  metaTone: {
    type: String as PropType<'subtle' | 'warning' | 'alert'>,
    default: 'subtle',
  },
  /** Initials for the default avatar; omit (and skip #leading) for avatar-less rows. */
  initials: {
    type: String,
    default: undefined,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  /** Non-pickable row, e.g. the folder the item is already in. */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** Multi-select look: leading checkbox instead of the trailing ink check. */
  checkbox: {
    type: Boolean,
    default: false,
  },
  /** Single-choice look: leading radio dot (the playbook template list). */
  radio: {
    type: Boolean,
    default: false,
  },
  /** Checkbox half-state: some children picked (a contract's file list). */
  indeterminate: {
    type: Boolean,
    default: false,
  },
  /** Child row under an expanded parent — indents past the parent's controls. */
  indent: {
    type: Boolean,
    default: false,
  },
  /** Pass true/false to render the expand caret; leave undefined for leaf rows. */
  expanded: {
    type: Boolean,
    default: undefined,
  },
  /**
   * Bold label — a row that heads a group of child rows (a contract above its
   * files), where the mock draws the parent one weight up from the children.
   */
  bold: {
    type: Boolean,
    default: false,
  },
});

// `toggle` is the caret; `select` is the row (which the caret must not trigger).
const emit = defineEmits<{ select: []; toggle: [] }>();

const rowBorder = gaiaNeutral.borderSoft;
const hoverWash = gaiaNeutral.surfaceHover;
const ink = gaiaNeutral.ink;
const surface = gaiaNeutral.surface;
const selectedBg = gaiaNeutral.surfaceSoft;
const caretColor = gaiaNeutral.textSubtle;
</script>

<template>
  <!-- Picker row from the share / move / reassign modals (CM list mock):
       avatar, label + meta, trailing count, ink check when selected. Bespoke
       list row — n-list/n-select options can't express this layout. -->
  <button
    type="button"
    class="gaia-picker-option"
    :class="{ selected: props.selected, disabled: props.disabled, indent: props.indent }"
    :disabled="props.disabled"
    @click="emit('select')"
  >
    <gaia-checkbox
      v-if="props.checkbox"
      :checked="props.selected"
      :indeterminate="props.indeterminate"
      tabindex="-1"
      style="pointer-events: none"
    />
    <n-flex
      v-if="props.expanded !== undefined"
      inline
      :size="0"
      :wrap="false"
      class="caret"
      role="button"
      :aria-label="props.expanded ? 'Collapse' : 'Expand'"
      @click.stop="emit('toggle')"
    >
      <svg
        :class="{ open: props.expanded }"
        width="11"
        height="11"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M4.5 2.5 8 6l-3.5 3.5" />
      </svg>
    </n-flex>
    <span v-else-if="props.radio" class="radio" :class="{ on: props.selected }" />
    <slot name="leading">
      <gaia-avatar v-if="props.initials" :size="26">{{ props.initials }}</gaia-avatar>
    </slot>
    <n-flex vertical :size="1" :style="{ minWidth: 0 }">
      <!-- The disabled recolor rides the same inline channel as `tone`, so it
           is a conditional prop rather than a CSS class. -->
      <gaia-text
        truncate
        size="dense"
        :weight="props.bold ? 'bold' : 'semibold'"
        :tone="props.disabled ? 'subtle' : 'ink'"
      >
        {{ props.label }}
      </gaia-text>
      <gaia-text v-if="props.meta" truncate size="meta" :tone="props.metaTone">
        {{ props.meta }}
      </gaia-text>
    </n-flex>
    <n-flex align="center" :size="10" :wrap="false" :style="{ marginLeft: 'auto', flex: 'none' }">
      <gaia-text
        v-if="$slots.trailing"
        size="micro"
        tone="muted"
        :style="{ fontVariantNumeric: 'tabular-nums' }"
      >
        <slot name="trailing" />
      </gaia-text>
      <!-- The ink check marks selection only when nothing on the left already
           does — a checkbox or radio row shows its state there. -->
      <n-flex
        v-if="props.selected && !props.checkbox && !props.radio"
        align="center"
        justify="center"
        :size="0"
        :wrap="false"
        class="check"
      >
        <svg
          width="9"
          height="9"
          viewBox="0 0 24 24"
          fill="none"
          :stroke="surface"
          stroke-width="3.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M4 12.5 9.5 18 20 6.5" />
        </svg>
      </n-flex>
    </n-flex>
  </button>
</template>

<style scoped>
.gaia-picker-option {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 0 12px;
  height: 48px;
  border: none;
  border-bottom: 1px solid v-bind(rowBorder);
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.gaia-picker-option:hover {
  background: v-bind(hoverWash);
}

.gaia-picker-option.selected {
  background: v-bind(selectedBg);
}

.gaia-picker-option.disabled {
  cursor: default;
}

/* Child rows sit under their parent's checkbox + caret. */
.gaia-picker-option.indent {
  padding-left: 32px;
  height: 44px;
}

.caret {
  flex: none;
  color: v-bind(caretColor);
}

.caret svg {
  transition: transform 0.15s;
}

.caret svg.open {
  transform: rotate(90deg);
}

/* The ink check disc: a fixed circle centring a 9px glyph. */
.check {
  width: 18px;
  height: 18px;
  flex: none;
  border-radius: 50%;
  background: v-bind(ink);
}
</style>
